/**
 * Product events, straight to Amplitude's HTTP V2 endpoint with a raw fetch — the same no-SDK rule
 * the service keeps for every vendor. The browser SDK would add a dependency to do exactly this POST.
 *
 * PRIVACY IS STRUCTURAL HERE, not a review comment. Nothing about a MEMBER ever leaves: no names, no
 * quotes, no profile ids — counts, verdicts and ranks only. Nothing about the BUYER leaves either
 * beyond a random device id: no email, no company name. `candidates.email` is the operator's card
 * only, and this file is one of the places that rule bites.
 *
 * The event names below are the whole taxonomy, and adding one HERE is what makes it exist — a typo'd
 * name in a call site would otherwise create a second, near-identical event that splits every funnel
 * built on the first.
 */

import { acquisitionProps } from '@/utils/acquisition'
import { ARM, EXPERIMENT } from '@/utils/abVariant'
import { assignmentChange, FUNNEL_ASSIGNMENT, FUNNEL_TAGS, type StoredAssignment } from '@/utils/funnel'
import { analyticsAllowed, analyticsEnvironment } from '@/utils/analyticsGate'
import { eventEnvelope } from '@/utils/analyticsEnvelope'
import { deviceId } from '@/utils/deviceId'
import { StorageKeys, storageService } from '@/utils/localStorageService'

export type AnalyticsEvent =
  /**
   * A screen was opened — the FIRST thing the funnel can count, and for a while the one thing it
   * could not. Everything below this line needs a click to happen, so paid traffic that landed and
   * left produced literally nothing: the pixels and GA both recorded the visit, Amplitude recorded
   * silence, and the product looked dead while the ads were running. Fired on every route change,
   * exactly where `gaScreen` already fires.
   */
  | 'page_viewed'
  /**
   * НАЗНАЧЕНИЕ ВОРОНКИ — сплит-тест 2026-09-28. Один раз на смену тройки «воронка × тест × пул» в этом
   * браузере, и первым событием загрузки (main.tsx, до рендера): `page_viewed` на этот вопрос не
   * отвечает — он стреляет на каждый экран, а дочерние эффекты (`onboarding_started`) успевают раньше
   * него. Это знаменатель сравнения 1 против 3 и ежедневная проверка долей: в пуле `a` ждём 50/50.
   * Свойства — utils/funnel.ts, assignmentChange: {funnel_type, funnel_name, funnel_test, split_pool,
   * previous_funnel, previous_pool, reason}.
   */
  | 'funnel_assigned'
  | 'demo_requested'
  | 'onboarding_started'
  /**
   * The four panes of /onboard, which is ONE route — so `page_viewed` fires once for the whole screen
   * and everything between arriving and finishing used to be invisible. Measured on the first day of
   * paid traffic: 24 started, 11 completed, and nothing could say which pane ate the other 13.
   */
  | 'onboard_step_viewed'
  /** The page read, every outcome. Five failure kinds existed and none of them was recorded, so a
      person whose site could not be read looked exactly like one who never typed. */
  | 'product_read_result'
  /** "Not my product" on the confirm pane: the product's own accuracy check on the model's read, and
      the single most useful number this screen can produce about the engine. */
  | 'product_read_rejected'
  /* Честная остановка на подтверждении (2026-10-06): «всё равно продолжить» и «описать иначе». */
  | 'product_poor_fit_continued'
  | 'product_poor_fit_describe'
  /* Чтение не назвало покупателя (отказ модели вычищен на сервере, 2026-10-07): описал словами / продолжил. */
  | 'product_no_buyer_describe'
  | 'product_no_buyer_continued'
  /* Карандаш у сегмента на ICP-листе (2026-10-07): {field: segment | removeSegment, surface: onboard |
     workspace}. Доля правленых листов — мера того, насколько чтение страницы попадает. */
  | 'product_icp_edited'
  /** Targeting (роли, функции, старшинство, индустрии от модели) не пришёл, и спека собрана из
      ролей сегментов. Три дня такого молчания стоили пяти прогонов на мёртвых должностях. Член
      союза был объявлен ДВАЖДЫ с разными комментариями (подбор переехал в первый шаг прогона
      2026-09-15) — слито 2026-09-21, событие всегда было одно. */
  | 'targeting_failed'
  /** A refused submit on the contact pane, by reason — including the DNS verdict, which silently
      withheld the Lead pixel too. */
  | 'contact_blocked'
  | 'onboarding_completed'
  | 'wizard_step_completed'
  | 'run_started'
  | 'shortlist_shown'
  /**
   * «Добавить соседние страны и искать снова» над выдачей (launch/geo/GeoNote.tsx): в спрошенной стране
   * мало людей, и человек расширил поиск в один тап. Свойства: `added` — сколько стран добавлено,
   * `countries` — какие. Мерит, помогает ли предложение соседей, или его не замечают.
   */
  | 'geo_widened'
  /**
   * ВХОД В ПРОДУКТ ДО ОПЛАТЫ — воронка 3 «погружение», 2026-09-21. Единственное новое имя события за
   * всю воронку: остальная лестница нарочно та же, что у воронок 1 и 2, иначе их нельзя сравнивать.
   *
   * `page_viewed('/home')` на этот вопрос не отвечает: он стреляет и на перезагрузку, и на возврат, а
   * в воронках 1 и 2 вообще случается ПОСЛЕ покупки. Здесь же вопрос ровно один — сколько людей
   * приняли предложение «Reach the contacts» вместо пейволла.
   *
   * `from` — та же ось, что у `paywall_opened.source` (launch/paywallSource.ts): в воронке 3 пейволла
   * на выдаче нет, и без этого свойства исчезла бы возможность ответить, КАКОЙ контрол уводит в
   * продукт. `funnel_at_entry` кладётся в запись поиска (utils/spec/searchHistory.ts), а не в это
   * событие: метку воронки событие несёт само.
   */
  | 'product_entered'
  /**
   * ОНБОРДИНГ ВНУТРИ КАБИНЕТА — воронка 4 (SaaS), 2026-09-22. Два шага после регистрации: `product`
   * (ссылка прочитана или описание принято) и `icp` (индустрии, локации, роли — подтверждены или
   * пропущены, `skipped`). Своё имя, а не `onboard_step_viewed`: те панели живут на /onboard и меряют
   * показ, а это — ЗАКРЫТИЕ шага уже внутри продукта, тем же человеком, что только что зарегистрировался.
   * `focus_shown` — коуч-марк над первым поиском показан; следующий шаг лестницы — `run_started`.
   */
  | 'product_onboarding_step'
  | 'focus_shown'
  /**
   * Книжка «как это работает» перед первым шагом онбординга в кабинете (2026-09-24) закрыта: `action`
   * — `finished` (дочитал до «Start with my product») или `skipped`, `page` — на какой из трёх страниц.
   * Одно событие на браузер: книжку показывают один раз (Home/onboarding/WelcomeBookStore.ts).
   */
  | 'welcome_book'
  /**
   * Оценка списка пальцем в окне поиска (Дима, 2026-10-08: «при появлении списка через 3 секунды
   * показывать палец вверх и вниз, чтобы люди оценили качество списка; постараемся таким образом трекать
   * NPS»; Home/sheet/rating). `action` — `shown` (вопрос показан, знаменатель), `up`, `down` или
   * `dismissed` (закрыл крестиком); ещё `run_id`, `rows` (сколько людей в списке), `paid` (купленный ли
   * поиск: до оплаты открыты три карточки) и `surface`: `workspace` — окно поиска в кабинете, `shortlist` —
   * шаг выдачи визарда, где список видят воронки 1–3 (с 2026-10-12). Один вопрос на список в браузере.
   */
  | 'list_rating'
  /**
   * Тур по кабинету после книжки (2026-10-06, воронка 4): шаг 1 — меню слева («This is your sales
   * platform») с записями разделов, шаг 2 — поле ссылки первого шага. `action`: `shown` (тур начался),
   * `next` (со шага 1 на шаг 2), `finished` (шаг 2 закрыт кнопкой или первым вводом в поле),
   * `skipped` («Skip the tour» или Esc); `step` — на каком шаге (1 или 2). `section_viewed` —
   * человек сам открыл раздел (`section`: dashboard · searches · crm), а не дождался автосмены.
   */
  | 'platform_tour'
  | 'signal_opened'
  /**
   * Открыта страница Billing кабинета (2026-10-09), `state` — none · active · empty · cancelled
   * (Home/billing/billingSummary.ts). Знаменатель продаж со страницы: `page_viewed` её не видит — Billing
   * открывается параметром `?view=billing`, а не сменой пути. Дальше лестница та же: оплата из Billing
   * приходит `paywall_opened` с `source: home_plans`, потом checkout_opened → payment_redirected →
   * order_confirmed. Одно событие на открытие страницы, а не на смену тарифа в списке.
   */
  | 'billing_viewed'
  /**
   * Подсказка «Start with …» над «Draft messages» первой карточки листа (Home/plan/DraftMark, 2026-10-09).
   * `action`: `shown` (знаменатель), `draft` (нажали «Draft for …» в подсказке), `later`, `self` (нажали
   * кнопку на карточке сами). Один раз на браузер. Сама сборка дальше — campaign_confirmed и plan_requested.
   */
  | 'draft_coach'
  /**
   * Пейволл открыт, и `source` говорит КАКОЙ КНОПКОЙ. Раньше четыре разных контрола карточки —
   * закрытая строка целиком, «Unlock the score», Chat и «Unlock to write the message» — приходили
   * одним значением `lead_card`, потому что все звали один `onUnlock`. По такому событию нельзя
   * ответить, что именно продаёт: список значений ниже и есть ответ.
   *
   * Значения: download_all · wall_gate · veiled_card · score · chat · write · email_field ·
   * workspace · launch_sequence.
   *
   * ПЕЙВОЛЛА КАК ОТДЕЛЬНОГО ЭКРАНА БОЛЬШЕ НЕТ — с 2026-09-15 лист «что в пакете» и есть чекаут:
   * «Buy» на нём создаёт платёж и уводит на форму шлюза, второго экрана пакетов между ними нет.
   * События НЕ переименованы и новых НЕ добавлено (решение продукта: воронка в Amplitude должна
   * остаться непрерывной с историей). Читать так:
   *
   *   paywall_opened {source}   — открыт чекаут (лист пакета); source — какой кнопкой пришли
   *   checkout_opened {qty}     — нажал Buy: пакет выбран, идёт уход к оплате
   *   package_selected {qty}    — стор чекаута зафиксировал пакет (тот же клик, следом)
   *   payment_redirected        — платёж создан, ушли на форму шлюза
   *   payment_failed            — шлюз отказал или почты нет; открывается запасной диалог
   *   order_confirmed           — вернулись с формы оплаченными (/thank-you)
   *
   * Пиксель Меты идёт той же лестницей: InitiateCheckout стреляет по Buy, Purchase — на /thank-you.
   */
  | 'paywall_opened'
  | 'checkout_opened'
  /**
   * Sent to the payment form — the last moment our code runs before the buyer lands on the external
   * gateway ("Intch Sales: N Warm Connections"), which is not our page and cannot fire an event of
   * its own. So this is measured HERE, at the redirect, and stands in for "reached the pay screen".
   * It closes the Choose-your-package funnel: checkout_opened (screen entered) → package_selected →
   * payment_redirected (left toward pay) → order_confirmed (returned paid). The gap between this and
   * order_confirmed is everything that happens on the gateway, which we cannot see.
   */
  | 'payment_redirected'
  | 'package_selected'
  | 'order_confirmed'
  | 'payment_failed'
  | 'first_message_requested'
  | 'first_message_result'
  /**
   * Воронка ОДНОЙ кампании, три шага, и до этого у неё был измерен только средний:
   *
   *   campaign_opened     нажали на карточке — экран кампании открылся, ничего ещё не потрачено;
   *   campaign_confirmed  нажали «Запустить» на самом экране — вот отсюда идёт трата;
   *   sequence_shown      доска нарисовалась, то есть человек увидел то, за чем шёл.
   *
   * Между первым и вторым лежит единственное подтверждение продукта, и раньше его отказы были
   * невидимы: `plan_requested` фиксировал уже подтверждённое, а тот, кто открыл экран и передумал,
   * не оставлял следа вообще. Между вторым и третьим лежит ожидание — сборка, отказ движка или
   * ошибка, — и его исход виден по паре с `plan_result`.
   */
  | 'campaign_opened'
  | 'campaign_confirmed'
  /** Кампания поставила людей в очередь на отправку — всех (`scope: all`) или одного (`one`). */
  | 'campaign_launched'
  /**
   * AI Sales (вариант B, 2026-10-13): `ai_sales_preview` — «See what it writes» на панели; `ai_sales_window` — окно
   * запуска открыто на купленном листе (`targets`); `ai_sales_started` — запущен (`contacts`); `ai_sales_next` — «Send to …»
   * повело к следующему по очереди. Дверь бесплатного листа — `paywall_opened` с source `ai_sales`.
   */
  | 'ai_sales_preview'
  | 'ai_sales_window'
  | 'ai_sales_started'
  | 'ai_sales_next'
  /* Автоотправка списка (2026-10-22): `ai_sales_started` несёт `mode` auto|manual; `ai_sales_auto_enrolled` — человек
     зачислен; `ai_sales_auto_refused` — сервер отказал (`kind`); `ai_sales_auto_rest` — «отправить остальных»
     (`contacts`); `ai_sales_auto_stopped` — «остановить всех» (`stopped`). */
  | 'ai_sales_auto_enrolled'
  | 'ai_sales_auto_refused'
  | 'ai_sales_auto_rest'
  | 'ai_sales_auto_stopped'
  /**
   * «Not sure? Book a demo» изнутри продукта (2026-10-14): календарь открыт, `source` — какая дверь (paywall,
   * paywall-phone, billing-plans, billing-summary), `direct` — без формы, имя и почта взяты из спеки. Записанную
   * встречу видно в Calendly; это событие — сколько людей дошло до календаря и откуда.
   */
  | 'demo_opened'
  /** Лист поиска выгружен в CSV. Только числа: сколько строк и сколько из них оценено. */
  | 'csv_downloaded'
  /**
   * CRM (2026-09-23): переключение таблица ↔ доска сделок и стадия, выставленная рукой (`stage` — какая,
   * или `auto` — возврат к автоматике). Только слово стадии: ни имени, ни id человека.
   */
  | 'crm_view_switched'
  | 'crm_stage_set'
  /**
   * Страница не загрузилась. `kind: stale_chunk` — вкладка пережила пересборку и перезагрузилась
   * сама (utils/lazyRoute.ts); `render` — экран сбоя показан человеку (AppErrorBoundary). Первое —
   * цена каждого деплоя в людях посреди воронки, второе — поломка, которую никто не заметил бы.
   */
  | 'app_crashed'
  /** Человек переключил активную компанию в «My company». */
  | 'company_switched'
  | 'sequence_shown'
  /* План аутрича под одного лида: запрошен, построен/отказан, открыт чат с текстом. */
  | 'plan_requested'
  | 'plan_result'
  | 'plan_chat_opened'
  | 'plan_variant'
  | 'plan_sent'
  /**
   * «Launch the sequence» → окно «как отправлять» (2026-10-07): `mode` auto | manual — что выбрали;
   * `auto` — была ли автоотправка доступна в этот момент (available) или почему нет (signIn, off,
   * checking, notNow). Доля auto среди всех запусков — ответ, нужна ли автоотправка вообще.
   */
  | 'sequence_launch_chosen'
  | 'plan_cadence'
  | 'plan_reply_saved'
  | 'plan_node_drafted'
  /**
   * Шаг цепочки правят из карточки или с доски (вариант B, 2026-10-12): `action` — text · day · add · remove · rewrite,
   * `surface` — card · board, `status` — ok · refused · failed. Одно событие на пять действий: какое из них люди делают,
   * читается одной разбивкой, а не пятью колонками.
   */
  | 'plan_step'
  /* Дешёвый проход по всему прогону: скольким людям он расставил порядок чтения и скольких пропустил. */
  | 'prefilter_done'
  | 'workspace_opened'
  /**
   * Someone left. Fired from the confirmation page, because the unsubscribe itself is handled
   * server-side where no product event can be raised — and carrying it as a UTM instead would have
   * put departures in the same acquisition report as returns, which is the one number the lifecycle
   * tagging exists to keep honest.
   */
  | 'unsubscribed'
  /**
   * Вход через аккаунт Intch. `code_sent` → `code_failed {reason}` / `signed_in {entry}`: разница
   * между первым и последним — это люди, которые не дошли до письма с кодом, и ради этой разницы шаг
   * и размечен. `entry` — онбординг, экран входа или молча по cookie с intch.org. `new_account` у
   * `signed_in` — аккаунт заведён этим кодом: регистрация отдельно от входа уже бывшего в Intch.
   * Это и есть «ввод кода — отдельное событие»: Lead остаётся на почте (utils/authGate.ts).
   */
  | 'auth_code_sent'
  | 'auth_code_failed'
  | 'auth_signed_in'
  | 'auth_signed_out'
  /**
   * Поиск, которым поделились ссылкой (2026-10-06), — три шага:
   *
   *   share_link_created    владелец выписал ссылку в окне поиска (`open` — сколько людей открыл);
   *   share_viewed          ссылку открыли (`open`, `matched`, `total`);
   *   share_unlock_clicked  на странице по ссылке нажали дверь оплаты (`source` — какую).
   *
   * Дальше та же лестница оплаты, что везде (checkout_opened → order_confirmed).
   */
  | 'share_link_created'
  | 'share_viewed'
  | 'share_unlock_clicked'

type Value = string | number | boolean | null

const KEY = (import.meta.env.VITE_AMPLITUDE_API_KEY as string | undefined) ?? ''

/* `deviceId` переехал в utils/deviceId.ts: его читает и назначение плеча A/B, и оба должны видеть
   один и тот же id. */

/** Amplitude sessionises on this: one id per page load is enough for funnel order. */
const sessionId = Date.now()

/**
 * КАКОЕ УСТРОЙСТВО — тремя корзинами, и это единственное, что о железе уезжает.
 *
 * Зачем: половина воронки может отваливаться на телефоне, и по нынешним событиям это неотличимо от
 * того, что она отваливается вообще. Свойство кладётся в КАЖДОЕ событие, а не только в page_viewed:
 * иначе по устройству режется вход и ничего дальше.
 *
 * ПОЧЕМУ НЕ USER-AGENT. Amplitude умеет разбирать его сама и рисовать свои графики устройств, но
 * строка UA — это отпечаток: связка версий браузера, ОС и сборки выделяет человека куда точнее, чем
 * нужно для ответа «телефон или нет». Здесь уезжает ОДНО слово из трёх возможных, и родные графики
 * устройств у Amplitude поэтому останутся пустыми — резать надо по свойству `device`.
 *
 * Как считается: сперва `navigator.userAgentData.mobile` — единственный сигнал, заведённый ровно под
 * этот вопрос (Chromium). Где его нет (Safari, Firefox) — грубый указатель без наведения, то есть
 * палец; десктоп с сенсорным экраном сюда не попадает, у него основной указатель точный. Планшет от
 * телефона отделяет короткая сторона ЭКРАНА, а не окна: 768 — та же граница, на которой ломается
 * раскладка карточки.
 *
 * Считается ОДИН раз за загрузку, как sessionId: устройство за сессию не меняется, а окно, ужатое до
 * 700px на десктопе, — это всё ещё десктоп, и события о нём не должны говорить «мобила».
 */
type DeviceKind = 'mobile' | 'tablet' | 'desktop'

const deviceKind = (): DeviceKind => {
  if (typeof window === 'undefined') return 'desktop'
  const hinted = (navigator as Navigator & { userAgentData?: { mobile?: boolean } }).userAgentData
  const coarse = window.matchMedia?.('(pointer: coarse)').matches ?? false
  const noHover = window.matchMedia?.('(hover: none)').matches ?? false
  if (hinted?.mobile !== true && !(coarse && noHover)) return 'desktop'
  const shortSide = Math.min(
    window.screen?.width || window.innerWidth,
    window.screen?.height || window.innerHeight,
  )
  return shortSide < 768 ? 'mobile' : 'tablet'
}

const DEVICE = deviceKind()

/**
 * ГДЕ ЧЕЛОВЕК — часовым поясом и языком, а НЕ собственным полем `country`.
 *
 * Страну Amplitude ставит сама: мы не шлём ни `ip`, ни `country`, запрос идёт из браузера напрямую,
 * и её GeoIP заполняет `Country` нативным свойством. Отправлять рядом своё, угаданное из локали,
 * значило бы завести вторую колонку «страна» с другими значениями — у человека из Берлина с русским
 * браузером они разойдутся, — и любой отчёт по странам после этого пришлось бы уточнять, по какой
 * именно колонке он построен. Одна страна на отчёт, и это GeoIP.
 *
 * Пояс и язык отвечают на то, чего GeoIP не знает:
 *   `timezone` — рабочие часы адресата и то, где он на самом деле, когда сидит через VPN;
 *   `locale`   — на каком языке браузер просит страницу, то есть стоит ли переводить.
 *
 * Обе величины грубые и не опознают человека — в отличие от строки User-Agent, которую мы по этой
 * же причине не шлём (см. deviceKind). Считаются один раз за загрузку: за сессию не меняются.
 */
const WHERE = (() => {
  if (typeof window === 'undefined') return { timezone: null, locale: null }
  try {
    return {
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
      locale: navigator.language || null,
    }
  } catch {
    /* Урезанный Intl или заблокированный доступ — событие уходит без них, а не не уходит вовсе. */
    return { timezone: null, locale: null }
  }
})()
/**
 * КАМПАНИЯ КАК СВОЙСТВО ЧЕЛОВЕКА, а не только свойство входа.
 *
 * `acquisitionProps()` подмешивается лишь в `page_viewed`, и это осознанно: гарантированные null в
 * каждом из тридцати с лишним событий — шум в отчёте. Но из-за этого по кампании нельзя было
 * сегментировать НИ ОДНО другое событие: воронка спасалась тем, что фильтр на первом шаге задаёт
 * когорту, а вот вопрос «какой ARPU у этой кампании» упирался в отсутствие метки на `order_confirmed`.
 *
 * Свойство ПОЛЬЗОВАТЕЛЯ решает это одним разом: Amplitude привязывает его к device_id и применяет ко
 * всей истории, поэтому резать по кампании можно любой отчёт, а форма событий не меняется.
 *
 * `$setOnce`, а НЕ `$set`, и это не стилистика: acquisition.ts реализует ПЕРВОЕ касание — за человека
 * заплатило то объявление, по которому он пришёл впервые. `$set` перезаписывал бы кампанию на каждом
 * возврате и приписывал бы покупку последнему объявлению, то есть ровно тому, которое не платило.
 *
 * Вместе с кампанией в `$setOnce` едет первое назначение воронки (utils/funnel.ts, funnelTags) — по
 * той же логике первого касания: сплит сравнивает людей по тому, КУДА их отправила первая монета.
 */
const ACQUISITION = acquisitionProps()

let eventSeq = 0

/**
 * Номер пользователя Intch — тот же, под которым человека знает основная платформа, поэтому воронка
 * здесь склеивается с его историей там. null — не вошёл: событие уходит по устройству, как раньше.
 */
let userId: string | null = null
export const setAnalyticsUser = (id: string | null) => {
  userId = id
}

/**
 * Fire and forget. `keepalive` lets the last event of a page survive the navigation that caused it —
 * order_confirmed fires exactly when the page is about to move on. Failures are swallowed: analytics
 * must never surface as a product error, and a lost event is a lost event.
 */
export const track = (event: AnalyticsEvent, props?: Record<string, Value>): boolean => {
  if (!KEY) return false
  /* Nothing leaves a machine or a dev host — see analyticsGate. A key in a local .env is for
     checking the wiring, not for reporting. */
  if (!analyticsAllowed()) return false
  void fetch('https://api2.amplitude.com/2/httpapi', {
    method: 'POST',
    keepalive: true,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      api_key: KEY,
      events: [
        {
          device_id: deviceId(),
          ...(userId ? { user_id: userId } : {}),
          session_id: sessionId,
          event_id: ++eventSeq,
          time: Date.now(),
          event_type: event,
          /* Что едет с каждым событием и почему — utils/analyticsEnvelope.ts: чистой функцией,
             чтобы правило метки сплит-теста проверялось без браузера. */
          ...eventEnvelope(
            {
              environment: analyticsEnvironment(),
              arm: ARM,
              experiment: EXPERIMENT,
              funnel: FUNNEL_TAGS,
              device: DEVICE,
              timezone: WHERE.timezone,
              locale: WHERE.locale,
              acquisition: ACQUISITION,
            },
            props,
          ),
          platform: 'Web',
        },
      ],
      /* Amplitude по умолчанию отбрасывает user_id короче пяти знаков, а номера Intch бывают «14». */
      ...(userId ? { options: { min_id_length: 1 } } : {}),
    }),
  }).catch(() => {})
  return true
}

const isStoredAssignment = (value: unknown): value is StoredAssignment => {
  const v = value as StoredAssignment | null
  return (
    v != null &&
    typeof v === 'object' &&
    typeof v.funnel === 'number' &&
    (v.test === null || typeof v.test === 'string') &&
    (v.pool === null || typeof v.pool === 'string')
  )
}

/**
 * `funnel_assigned` — один раз на смену назначения (см. тип события выше). Зовётся из main.tsx ДО
 * рендера, поэтому идёт первым событием загрузки, раньше любого `page_viewed`.
 *
 * Запоминается только ОТПРАВЛЕННОЕ: на локальной машине, без ключа или за закрытым гейтом ничего не
 * пишется, иначе первый настоящий заход с прода не узнал бы, что назначения ещё не видели.
 */
export const reportFunnelAssignment = () => {
  const raw = storageService.getItem<unknown>(StorageKeys.funnelAssigned)
  const change = assignmentChange(isStoredAssignment(raw) ? raw : null, FUNNEL_ASSIGNMENT)
  if (!change) return
  if (track('funnel_assigned', change.props)) storageService.setItem(StorageKeys.funnelAssigned, change.next)
}

/**
 * A screen was opened. Carries the campaign that brought the visit on EVERY screen, not just the
 * first — `utm_*` lives in the landing URL alone, and a funnel that can only attribute step one
 * cannot say which ad paid for step five. The record itself is owned by `utils/acquisition.ts`,
 * because the server now needs the same one: an event at the vendor cannot say WHO arrived.
 */
export const trackPageView = (path: string, screen: number | null) =>
  track('page_viewed', { path, screen, ...acquisitionProps() })
